import { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { Dayjs } from 'dayjs'
import { CSSProperties, useEffect, useMemo, useState } from 'react'

import { helper } from 'helper'
import { shiftQuestionnaireApi } from 'hooks/api/shiftQuestionnaire'
import { shiftQuestionnaireAnswerApi } from 'hooks/api/shiftQuestionnaireAnswer'
import AvailabilityFormComponent from 'pages/answer/shift/components/availabilityFormComponent'
import LineTagComponent from 'pages/answer/shift/components/lineTagComponent'
import { useIsHoliday } from 'store/holidaySlice'
import { FIX_SHIFT, FixShift } from 'types/securityDetail'
import { ShiftAnswer } from 'types/shiftAnswer'
import { colors } from 'variables'

interface Props {
  token: string
}

const apiCall = (token: string) => ({
  getQuestionnaire: async () => {
    const response = await shiftQuestionnaireApi().filterByToken(token)
    return response.data
  },
  create: async (shiftQuestionnaireId: string, answers: ShiftAnswer[]) => {
    await shiftQuestionnaireAnswerApi().create(
      token,
      shiftQuestionnaireId,
      answers,
    )
  },
})

const fixShiftAsBool = (value?: FixShift): boolean | null => {
  if (value === undefined) return null
  switch (value) {
    case FIX_SHIFT.OK:
      return true
    case FIX_SHIFT.NG:
      return false
    case FIX_SHIFT.UNSELECT:
      return null
  }
}

const dateFormat = 'M月D日(ddd)'

const Mode = {
  EXPIRED: 0,
  SUBMITTED: 1,
  UNANSWERED: 2,
} as const
type ModeKey = (typeof Mode)[keyof typeof Mode]

export default function ShiftAnswerComponent(props: Props) {
  const [days, setDays] = useState<Dayjs[]>([])
  const [answerMap, setAnswerMap] = useState<(boolean | null)[]>([])
  const [shiftQuestionnaireId, setShiftQuestionnaireId] = useState('')
  const [respondent, setRespondent] = useState('')
  const [securityFields, setSecurityFields] = useState<string[]>([])
  const [isSubmitted, setIsSubmitted] = useState<boolean | null>(null)
  const [isExpired, setIsExpired] = useState(false)
  const isHoliday = useIsHoliday()

  const mode = useMemo<ModeKey>(() => {
    if (isSubmitted) return Mode.SUBMITTED
    if (isExpired) return Mode.EXPIRED
    return Mode.UNANSWERED
  }, [isSubmitted, isExpired])

  const fetchData = async (token: string) => {
    apiCall(token)
      .getQuestionnaire()
      .then((data) => {
        setRespondent(data.respondent)
        setSecurityFields(data.securityFieldNames)
        setShiftQuestionnaireId(data.id)
        setIsSubmitted(data.recentAnswer != null)
        setIsExpired(data.expire.isBefore(helper.day.today()))
        const days = helper.day.getDays(data.year, data.month)
        setDays(days)

        const answer: typeof answerMap = []
        if (data.recentAnswer == null) {
          // 回答用
          days.forEach((date, i) => {
            answer[i] = fixShiftAsBool(data.template[helper.day.dayIndex(date)])
          })
        } else {
          // 回答済み
          data.recentAnswer.answers.forEach((v) => {
            answer[helper.day.dateIndex(v.date)] = v.availability
          })
        }
        setAnswerMap(answer)
      })
  }

  useEffect(() => {
    fetchData(props.token).then()
  }, [props.token])

  const handleChangeAnswer = (dateIndex: number, value: boolean | null) => {
    setAnswerMap((prev) => {
      const newMap = [...prev]
      newMap[dateIndex] = value
      return newMap
    })
  }

  const handleSubmit = () => {
    // oxのみ許容
    // 1つでも未回答があればアラート
    if (Array.from(answerMap.values()).some((x) => x === null)) {
      alert('未回答の日があります')
      return
    }

    const answers: ShiftAnswer[] = answerMap.map((value, dateIndex) => {
      const date = days[dateIndex]
      return {
        date: date.add(date.utcOffset(), 'minutes'),
        availability: value as boolean,
      }
    })

    setIsSubmitted(true)
    apiCall(props.token)
      .create(shiftQuestionnaireId, answers)
      .then(() => {
        alert('提出しました')
        fetchData(props.token).then()
        // TODO 回答内容をメールで自身に送り返す
      })
      .catch(() => {
        alert('提出に失敗しました')
        setIsSubmitted(false)
      })
  }

  return (
    <Box style={containerStyle}>
      {/*依頼情報*/}
      <Box style={infoStyle}>
        <Box>
          <LineTagComponent text="名前" />
          <Typography style={textStyle}>{respondent}</Typography>
        </Box>

        <Box>
          <LineTagComponent text="警備先" />
          <Typography style={textStyle}>{securityFields.join(', ')}</Typography>
        </Box>
      </Box>

      {/*回答*/}
      <Box sx={answerStyle}>
        {answerMap.map(
          (answer, dateIndex) =>
            answer !== undefined && (
              <Box key={`answer-${dateIndex}`} style={answerRowStyle}>
                <Typography
                  style={{
                    ...textStyle,
                    ...weekColorStyle(
                      days[dateIndex],
                      isHoliday(days[dateIndex]),
                    ),
                  }}
                >
                  {days[dateIndex].format(dateFormat)}
                </Typography>
                <AvailabilityFormComponent
                  value={answer}
                  onChange={(v) => handleChangeAnswer(dateIndex, v)}
                />
              </Box>
            ),
        )}
      </Box>

      {/*提出ボタン*/}
      <Box style={submitStyle}>
        {mode == Mode.SUBMITTED && (
          <Button
            color="primary"
            variant="contained"
            disabled
            style={submitButtonStyle}
          >
            回答済み
          </Button>
        )}

        {mode == Mode.EXPIRED && (
          <Button
            color="primary"
            variant="contained"
            disabled
            style={submitButtonStyle}
          >
            期限切れ
          </Button>
        )}

        {mode == Mode.UNANSWERED && (
          <Button
            color="primary"
            variant="contained"
            style={submitButtonStyle}
            disabled={isSubmitted == null}
            onClick={handleSubmit}
          >
            提出
          </Button>
        )}
      </Box>
    </Box>
  )
}

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  padding: '16px',
  boxSizing: 'border-box',
  overflow: 'hidden',
  // height: '100%',
}

const infoStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  boxSizing: 'border-box',
  borderBottom: '2px solid #333333',
}

const answerStyle: SxProps = {
  flex: 1,
  overflowY: 'scroll',
  '& > :not(:last-of-type)': {
    borderBottom: '1px dashed #333333',
  },
}

const answerRowStyle: CSSProperties = {
  display: 'grid',
  width: '100%',
  gridTemplateColumns: '130px 1fr',
  alignItems: 'center',
  padding: '8px 0',
}

const submitStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: '16px',
}

const submitButtonStyle: CSSProperties = {
  fontSize: 24,
  width: 180,
  height: 64,
}

const textStyle: CSSProperties = {
  fontSize: '20px',
}

const weekColorStyle = (date: Dayjs, isHoliday: boolean): CSSProperties => {
  if (date.day() === 0 || isHoliday) {
    return {
      // 日曜日
      color: colors.WEEK.SUN_COLOR,
    }
  }

  if (date.day() === 6) {
    return {
      // 土曜日
      color: colors.WEEK.SAT_COLOR,
    }
  }

  return {
    // 平日 (色の上書き無し)
  }
}
